import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMaintenanceOpen, setIsMaintenanceOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    // Si on n'est pas sur la home, naviguer d'abord vers la home
    if (location.pathname !== '/') {
      navigate('/');
      // Attendre que la navigation soit terminée puis scroller
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Si déjà sur la home, scroller directement
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
    setIsMaintenanceOpen(false);
  };

  const maintenanceItems = [
    { label: 'Maintenance des Quais', path: '/maintenance-quai' },
    { label: 'Maintenance Industrielle', path: '/maintenance-industrielle' },
    { label: 'Soudage sur Site', path: '/soudage-reparation-metallique' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#020d2a]/95 backdrop-blur-md shadow-lg' 
          : 'bg-gradient-to-b from-[#020d2a]/90 to-transparent'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-gray-300 hover:text-orange-400 transition-colors font-medium"
            >
              Accueil
            </button>
            <button
              onClick={() => scrollToSection('qui-sommes-nous')}
              className="text-gray-300 hover:text-orange-400 transition-colors font-medium"
            >
              Qui sommes-nous ?
            </button>
            <button
              onClick={() => scrollToSection('products')}
              className="text-gray-300 hover:text-orange-400 transition-colors font-medium"
            >
              Produits
            </button>
            
            {/* Dropdown Maintenance */}
            <div 
              className="relative"
              onMouseEnter={() => setIsMaintenanceOpen(true)}
              onMouseLeave={() => setIsMaintenanceOpen(false)}
            >
              <button
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium flex items-center gap-1"
              >
                Maintenance
                <ChevronDown className={`w-4 h-4 transition-transform ${isMaintenanceOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isMaintenanceOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-slate-200">
                  {maintenanceItems.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      className="block px-4 py-3 text-slate-700 hover:bg-orange-50 hover:text-[#FF6600] transition-colors"
                      onClick={() => {
                        setIsMaintenanceOpen(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollToSection('realisations')}
              className="text-gray-300 hover:text-orange-400 transition-colors font-medium"
            >
              Réalisations
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-300 hover:text-orange-400 transition-colors font-medium"
            >
              Contact
            </button>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center space-x-6">
            <div className="flex items-center space-x-4 text-sm">
              <a href="tel:0659452562" className="flex items-center text-gray-300 hover:text-orange-400 transition-colors">
                <Phone className="w-4 h-4 mr-2" />
                <span>06 59 45 25 62</span>
              </a>
            </div>
            <Button 
              onClick={() => scrollToSection('quote')}
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Devis Gratuit
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-[#020d2a] border-t border-[#020d2a] py-4 px-2">
            <nav className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2"
              >
                Accueil
              </button>
              <button
                onClick={() => scrollToSection('qui-sommes-nous')}
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2"
              >
                Qui sommes-nous ?
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2"
              >
                Produits
              </button>
              
              {/* Mobile Maintenance submenu */}
              <div>
                <button
                  onClick={() => setIsMaintenanceOpen(!isMaintenanceOpen)}
                  className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2 w-full flex items-center justify-between"
                >
                  Maintenance
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMaintenanceOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMaintenanceOpen && (
                  <div className="pl-6 mt-2 space-y-2">
                    {maintenanceItems.map((item, index) => (
                      <Link
                        key={index}
                        to={item.path}
                        className="block text-gray-400 hover:text-orange-400 transition-colors text-sm px-4 py-2"
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setIsMaintenanceOpen(false);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('realisations')}
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2"
              >
                Réalisations
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-gray-300 hover:text-orange-400 transition-colors font-medium text-left px-4 py-2"
              >
                Contact
              </button>
              <div className="border-t border-gray-700 pt-3 mt-3">
                <a href="tel:0659452562" className="flex items-center text-gray-300 px-4 py-2">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>06 59 45 25 62</span>
                </a>
                <Button 
                  onClick={() => scrollToSection('quote')}
                  className="bg-orange-500 hover:bg-orange-600 text-white w-full mt-3"
                >
                  Devis Gratuit
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
